import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { Controller } from 'react-hook-form'

import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Button from '@/elements/Button'
import FormInput from '@/elements/FormInput'
import { LockupWhite } from '@/assets'
import toast from '@/utils/toast'
import { forgotPassword, resetPassword } from '@/api/auth'
import useResetPasswordForm from '@/hooks/forms/useResetPasswordForm'
import useBreakpoints from '@/hooks/useBreakpoints'
import { useTheme } from '@/utils/theme'

type Step = 'email' | 'reset'

function ResetPassword() {
  const navigate = useNavigate()
  const { isMobile } = useBreakpoints()
  const { theme } = useTheme()
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')

  const form = useResetPasswordForm()

  const sendEmailMutation = useMutation({
    mutationFn: forgotPassword,
    onSuccess: () => {
      toast.success('Verification code sent to your email')
      setStep('reset')
    },
    onError: () => {
      toast.error('Failed to send reset code')
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      toast.success('Password reset successfully')
      setTimeout(() => {
        navigate('/')
      }, 2000)
    },
    onError: () => {
      toast.error('Failed to reset password')
    },
  })

  return (
    <Flex
      component='main'
      align='center'
      justify='center'
      gap={!isMobile ? 6 : 4}
      py={!isMobile ? 7 : 5}
      px={4}
      className='auth'
      style={{ minHeight: '100svh' }}
    >
      <LockupWhite style={{ width: 162, maxWidth: '80%' }} />

      <Flex
        align='center'
        gap={2}
        grow
        style={{
          width: !isMobile ? 306 : '100%',
          maxWidth: '100%'
        }}
      >
        <Flex align='center' pb={4}>
          <Text variant='h1' style={{ fontSize: '2rem' }} align='center'>
            Reset Password
          </Text>
          <Text variant='textblock' color='black-light-80' align='center'>
            {step === 'email'
              ? 'Enter your email to receive a verification code.'
              : 'Enter the code and choose a new password.'}
          </Text>
        </Flex>

        {step === 'email' && (
          <Flex gap={3} wfull>
            <FormInput
              type='email'
              label='Email address'
              placeholder='Enter your email'
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{ width: '100%' }}
              required
            />
            <Flex className='form-controls' pt={2}>
              <Button
                variant='accent'
                onClick={() => sendEmailMutation.mutate(email)}
                disabled={!email || sendEmailMutation.isPending}
                fullWidth
              >
                {sendEmailMutation.isPending ? 'Sending…' : 'Send Reset Code'}
              </Button>
            </Flex>
          </Flex>
        )}

        {step === 'reset' && (
          <form
            onSubmit={form.handleSubmit(data =>
              resetPasswordMutation.mutate({
                email,
                code: data.code,
                newPassword: data.newPassword,
              })
            )}
          >
            <Flex gap={3} wfull>
              <Controller
                control={form.control}
                name='code'
                render={({ field }) => (
                  <FormInput
                    {...field}
                    label='Verification Code'
                    placeholder='Enter the code from your email'
                    error={form.formState.errors.code?.message}
                    dirty={!!form.formState.dirtyFields.code}
                    style={{ width: '100%' }}
                  />
                )}
              />
              <Flex color='gray-light-20' wfull style={{ height: '1px' }} />
              <Controller
                control={form.control}
                name='newPassword'
                render={({ field }) => (
                  <FormInput
                    {...field}
                    type='password'
                    label='New Password'
                    placeholder='Enter your new password'
                    error={form.formState.errors.newPassword?.message}
                    dirty={!!form.formState.dirtyFields.newPassword}
                    style={{ width: '100%' }}
                  />
                )}
              />
              <Controller
                control={form.control}
                name='passwordConfirm'
                render={({ field }) => (
                  <FormInput
                    {...field}
                    type='password'
                    label='Confirm Password'
                    placeholder='Re-enter your new password'
                    error={form.formState.errors.passwordConfirm?.message}
                    dirty={!!form.formState.dirtyFields.passwordConfirm}
                    style={{ width: '100%' }}
                  />
                )}
              />
              <Flex className='form-controls' pt={2}>
                <Button
                  submit
                  variant='accent'
                  disabled={resetPasswordMutation.isPending}
                  fullWidth
                >
                  {resetPasswordMutation.isPending ? 'Resetting…' : 'Reset Password'}
                </Button>
              </Flex>
            </Flex>
          </form>
        )}

        <Button
          fullWidth
          variant={theme === 'dark' ? 'secondary' : 'primary'}
          onClick={() => navigate('/')}
        >
          Cancel
        </Button>
      </Flex>
    </Flex>
  )
}

export default ResetPassword
